<template>
  <div class="co-actions-container">
    <!-- 拆股／合股  -->
    <div v-if="splitSource" class="action">
      <span class="title-date">{{
        splitTitle + ': ' + formatDate(splitSource.executeDate) + splitInfo
      }}</span>
      <span class="info" @click="splitAlert">
        <icon name="circle-alert" />
      </span>
    </div>
    <!-- 除权日  -->
    <div v-if="showDividendNote" class="action">
      <span class="title-date">{{
        $t('trade.co-actions.exit-right') + ': ' + dividendDate + dividendAmount
      }}</span>
      <span class="info" @click="dividendAlert">
        <icon name="circle-alert" />
      </span>
    </div>
    <!-- 财报  -->
    <div v-if="isShowEarnNote" class="action">
      <span class="title-date">{{
        $t('quote.earnings-calendar-date') + ': ' + earnDate
      }}</span>
    </div>
    <!-- 代码改变  -->
    <div v-if="stock.symbolChange" class="action">
      <span class="title-date">{{ codeChangeInfo }}</span>
      <span class="info" @click="symbolChangeAlert">
        <icon name="circle-alert" />
      </span>
    </div>
  </div>
</template>
<script>
import Icon from 'com/Icon'
import { fetchStockCorporateActions } from 'apis/stock'
import { MARKET_TYPE, SEC_TYPE } from 'const/stock'
import { TIME_ZONE } from 'const/common'
import Modal from 'com/Modal'
import { mapGetters } from 'vuex'

export default {
  name: 'CompanyActions',
  components: {
    Icon
  },
  props: {
    stock: {
      required: true
    }
  },
  data() {
    return {
      coActions: null
    }
  },
  mounted() {
    this.coActions = null
    this.getCoActions()
  },
  watch: {
    'stock.symbol'() {
      this.coActions = null
      this.getCoActions()
    },
    language() {
      this.coActions = null
      this.getCoActions()
    }
  },
  computed: {
    ...mapGetters(['language']),
    isAStock() {
      return this.stock && MARKET_TYPE.A.includes(this.stock.market)
    },
    isOption() {
      return this.stock && this.stock.secType === SEC_TYPE.OPT
    },
    timezone() {
      const market = this.stock && this.stock.market
      return TIME_ZONE[market]
    },
    timezoneLabel() {
      const market = this.stock && this.stock.market
      const labelMap = {
        [MARKET_TYPE.US]: 'trade.co-actions.us-time',
        [MARKET_TYPE.HK]: 'trade.co-actions.hk-time',
        [MARKET_TYPE.UK]: 'trade.co-actions.uk-time',
        [MARKET_TYPE.SH]: 'trade.co-actions.bj-time',
        [MARKET_TYPE.SZ]: 'trade.co-actions.bj-time',
        [MARKET_TYPE.CN]: 'trade.co-actions.bj-time'
      }
      return this.$t(labelMap[market] || 'trade.co-actions.bj-time')
    },
    splitSource() {
      if (this.stock.split) {
        return this.stock.split
      }
      if (this.coActions) {
        return this.coActions.split
      }
    },
    splitIsMerge() {
      if (this.splitSource) {
        const { toFactor, forFactor } = this.splitSource
        return toFactor < forFactor
      }
    },
    splitTitle() {
      if (this.splitSource) {
        return this.splitIsMerge
          ? this.$t('trade.co-actions.merge-date')
          : this.$t('trade.co-actions.split-date')
      }
    },
    codeChangeInfo() {
      const res =
        this.$t('trade.co-actions.symbol-change') +
        ': ' +
        this.executeDate +
        ', ' +
        this.$t('trade.co-actions.symbol-change-to') +
        this.stock.symbolChange.newSymbol
      return res
    },
    splitInfo() {
      let info = ''
      if (!this.isOption) {
        const { toFactor, forFactor } = this.splitSource
        const mergeOrSplit = this.splitIsMerge
          ? this.$t('trade.co-actions.merge')
          : this.$t('trade.co-actions.split')
        info = `, ${forFactor}${mergeOrSplit}${toFactor}`
      }
      return info
    },
    showDividendNote() {
      if (!this.coActions) return
      if (this.coActions.dividend) return true
      if (this.coActions.data && this.coActions.data.dividend) return true
    },
    executeDate() {
      const symbolChange = this.stock.symbolChange
      return this.formatDate(symbolChange.executeDate)
    },
    dividendAmount() {
      const amountInfo = this.isOption
        ? ''
        : `, ${this.$t('trade.co-actions.stock-dividend')}${
            this.coActions.dividend.amount
          }`
      return amountInfo
    },
    dividendDate() {
      if (!this.coActions) return
      let date = null
      if (this.coActions.dividend) {
        date =
          this.coActions.dividend.executeDate ||
          this.coActions.dividend.payableDate
      } else if (this.coActions.data.dividend) {
        date = this.coActions.data.dividend.payableDate
      }
      return this.formatDate(date)
    },
    hasEarnNote() {
      return (
        this.coActions &&
        this.coActions.earningsCalendar &&
        this.coActions.earningsCalendar.date
      )
    },
    earnDate() {
      if (!this.coActions.earningsCalendar) return false
      if (!this.coActions.earningsCalendar.date) return ''
      const reporterDate = this.formatDate(this.coActions.earningsCalendar.date)
      const reporterTime = this.coActions.earningsCalendar.time || ''
      return `${reporterDate} ${reporterTime}`
    },
    isRightsOffer() {
      return !!this.stock.rights
    },
    rightOfferDate() {
      if (!this.isRightsOffer) return
      return (
        this.stock.rights.firstDealingDate || this.stock.rights.lastDealingDate
      )
    },
    isShowEarnNote() {
      return this.hasEarnNote && !this.isOption
    }
  },
  methods: {
    formatDate(date, mode = 0) {
      return this.$options.filters.formatDate(date, mode, this.timezone)
    },
    getCoActions() {
      let { symbol, market } = this.stock
      if (this.isRightsOffer) {
        symbol = this.stock.rights.symbol
      }
      fetchStockCorporateActions({ market, symbol }).then(resp => {
        if (resp.data.ret !== 0) return
        this.coActions = resp.data.data
      })
    },
    splitAlert() {
      let title, content
      const date = this.formatDate(this.splitSource.executeDate)
      const newNum = this.splitSource.toFactor
      const oldNum = this.splitSource.forFactor
      if (this.isOption) {
        title = this.$t('trade.co-actions.split-option-title')
        content = `<div>${this.timezoneLabel} ${date}，${this.$t(
          'trade.co-actions.split-option'
        )}`
        this.showAlert(title, content)
      } else {
        if (this.splitIsMerge) {
          title = this.$t('trade.co-actions.merge-title')
          content = `<div>${this.timezoneLabel} ${date}，${oldNum}${this.$t(
            'trade.co-actions.merge'
          )}${newNum}</div>`
        } else {
          title = this.$t('trade.co-actions.split-title')
          content = `<div>${this.timezoneLabel} ${date}，${oldNum}${this.$t(
            'trade.co-actions.split'
          )}${newNum}</div>`
        }
        this.showAlert(title, content)
      }
    },
    dividendAlert() {
      const title = this.$t('trade.co-actions.exit-right-title')
      const timezone = this.timezoneLabel
      const date = this.dividendDate
      const amount = this.coActions.dividend.amount
      let content
      if (this.isRightsOffer) {
        content = `<div>${this.$t('trade.co-actions.right-offer-div')}</div>`
        this.showAlert(title, content)
      }
      if (this.isOption) {
        content = `<div>${timezone} ${date}, ${this.$t(
          'trade.co-actions.option-div'
        )}</div>`
        this.showAlert(title, content)
      } else {
        content = `<div>${timezone} ${date}, ${this.$t(
          'trade.co-actions.each-div'
        )} ${amount}</div>`
        this.showAlert(title, content)
      }
    },
    symbolChangeAlert() {
      const title = this.$t('trade.co-actions.change-title')
      const date = this.formatDate(this.stock.symbolChange.executeDate)
      const newSymbol = this.stock.symbolChange.newSymbol
      const content = `<div>${this.$t(
        'trade.co-actions.change-intro'
      )}</div><div>${this.timezoneLabel} ${date}, ${this.$t(
        'trade.co-actions.change-to'
      )} ${newSymbol}</div>`

      this.showAlert(title, content)
    },
    rightOfferAlert() {
      const title = this.$t('trade.co-actions.right-offer-title')
      const date1 = this.formatDate(this.stock.rights.firstDealingDate)
      const date2 = this.formatDate(this.stock.rights.lastDealingDate)
      const content = `<div>${this.$t(
        'trade.co-actions.right-offer-intro'
      )}</div><div>${this.$t(
        'trade.co-actions.right-offer-begin'
      )} ${date1} ${this.$t('trade.co-actions.until')} ${date2}, ${this.$t(
        'trade.co-actions.right-offer-end'
      )}</div>`

      this.showAlert(title, content)
    },
    showAlert(title, content) {
      Modal.open({
        title,
        content,
        cancelButton: false
      })
    }
  }
}
</script>
<style lang="less" scoped>
.co-actions-container {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  margin-top: 6px;
  .action {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    .title-date {
      color: var(--active-color);
    }
    .info {
      color: var(--sub-text-color);
      height: 14px;
      margin-left: 5px;
      cursor: pointer;
      svg {
        display: block;
        height: 14px;
      }
    }
  }
}
</style>
